/**
 * Infrastructure Intelligence — Encroachment Detection  (Step 8c of the 8-step intelligence loop)
 *
 * Detect and track unauthorised construction, encroachment, and land-use change within
 * infrastructure right-of-way (RoW) corridors using NDBI change detection from Sentinel-2.
 * Each encroachment case can be escalated to a compliance response or legal referral.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  AlertOctagon, RefreshCw, CheckCircle2, XCircle, ShieldAlert, Zap,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Severity      = 'low' | 'medium' | 'high' | 'critical';
type AnomalyStatus = 'pending' | 'investigating' | 'resolved' | 'false_positive';

interface EncroachmentAnomaly {
  id: string;
  boundary_name: string;
  boundary_id: string;
  anomaly_type: string;
  severity: Severity;
  status: AnomalyStatus;
  change_percentage: number | string;
  affected_area_hectares?: number;
  description?: string;
  detection_date: string;
  resolution_notes?: string;
}

// ─── Escalation options ───────────────────────────────────────────────────────

const ESCALATION_ACTIONS = [
  {
    id: 'issue_notice',
    label: 'Issue Encroachment Notice',
    icon: ShieldAlert,
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[ENCROACHMENT: Notice issued] ',
    badgeText: 'legal notice',
    badgeClass: 'bg-orange-100 text-orange-700',
  },
  {
    id: 'legal_referral',
    label: 'Refer to Legal / Regulator',
    icon: AlertOctagon,
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[ENCROACHMENT: Legal referral] ',
    badgeText: 'legal action',
    badgeClass: 'bg-red-100 text-red-700',
  },
  {
    id: 'monitor',
    label: 'Monitor — No Immediate Action',
    icon: CheckCircle2,
    newStatus: 'pending' as AnomalyStatus,
    notePrefix: '[ENCROACHMENT: Monitoring only] ',
    badgeText: null,
    badgeClass: '',
  },
  {
    id: 'false_positive',
    label: 'Reject — False Positive',
    icon: XCircle,
    newStatus: 'false_positive' as AnomalyStatus,
    notePrefix: '[ENCROACHMENT: False positive] ',
    badgeText: null,
    badgeClass: '',
  },
];

// ─── Style maps ───────────────────────────────────────────────────────────────

const SEVERITY_STYLE: Record<Severity, string> = {
  low:      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  medium:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  high:     'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  critical: 'bg-red-200 text-red-900 font-bold dark:bg-red-900/60 dark:text-red-200',
};

const STATUS_STYLE: Record<AnomalyStatus, string> = {
  pending:        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  investigating:  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  resolved:       'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  false_positive: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  pending:        'Pending',
  investigating:  'Action Taken',
  resolved:       'Resolved',
  false_positive: 'False Positive',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const EncroachmentDetectionPage: React.FC = () => {
  const [anomalies, setAnomalies]   = useState<EncroachmentAnomaly[]>([]);
  const [assets, setAssets]         = useState<{ id: string; name: string }[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [loading, setLoading]       = useState(true);
  const [monitoring, setMonitoring] = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [filterAsset,    setFilterAsset]    = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');

  const [reviewing, setReviewing]   = useState<EncroachmentAnomaly | null>(null);
  const [actionId, setActionId]     = useState('');
  const [actionNote, setActionNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyRes, statsRes] = await Promise.all([
        anomalyApi.getAnomalies({
          severity:   filterSeverity || undefined,
          status:     filterStatus   || undefined,
          boundaryId: filterAsset    || undefined,
          vertical:   'infrastructure',
        }),
        anomalyApi.getStats('infrastructure'),
      ]);
      // Filter to encroachment/unauthorized-construction anomaly types
      const all = (anomalyRes.anomalies ?? []) as EncroachmentAnomaly[];
      setAnomalies(
        all.filter((a) => /encroach|unauthori|ndbi|build|construct/i.test(a.anomaly_type))
      );
      setStats(statsRes);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load encroachment data');
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, filterStatus, filterAsset]);

  useEffect(() => {
    load();
    satelliteBoundariesApi.getAll()
      .then((res) => {
        const all = res.data?.boundaries ?? [];
        setAssets(all.filter((b: any) => b.metadata?.vertical === 'infrastructure'));
      })
      .catch(() => {});
  }, [load]);

  const handleRunMonitoring = async () => {
    setMonitoring(true); setError(null);
    try {
      const res = await anomalyApi.runMonitoring('infrastructure');
      setSuccess(`NDBI scan complete — ${res.anomaliesDetected ?? 0} encroachment event(s) detected`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Monitoring failed');
    } finally {
      setMonitoring(false);
    }
  };

  const handleEscalate = async () => {
    if (!reviewing || !actionId) return;
    const action = ESCALATION_ACTIONS.find((a) => a.id === actionId)!;
    setSubmitting(true);
    try {
      await anomalyApi.updateStatus(reviewing.id, {
        status: action.newStatus,
        notes:  `${action.notePrefix}${actionNote}`.trim(),
      });
      setSuccess(`Decision recorded: ${action.label}`);
      setReviewing(null);
      setActionId('');
      setActionNote('');
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ?? e?.message ?? 'Failed to save decision');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = anomalies.filter((a) => a.status === 'pending').length;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertOctagon className="w-7 h-7 text-red-500" /> Encroachment Detection
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Step 8c — Detect unauthorised construction and RoW encroachment via NDBI change analysis
          </p>
          {pendingCount > 0 && (
            <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
              <Zap className="w-3 h-3" /> {pendingCount} encroachment event{pendingCount !== 1 ? 's' : ''} pending
            </span>
          )}
        </div>
        <Button onClick={handleRunMonitoring} loading={monitoring} variant="outline">
          <Zap className="w-4 h-4 mr-1" /> Scan RoW (NDBI)
        </Button>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Stats ── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Encroachments', value: anomalies.length,                    col: 'text-gray-900 dark:text-white' },
            { label: 'Critical',            value: anomalies.filter(a => a.severity === 'critical').length, col: 'text-red-700 dark:text-red-400' },
            { label: 'Pending Action',      value: pendingCount,                         col: 'text-amber-700 dark:text-amber-400' },
            { label: 'Action Taken',        value: anomalies.filter(a => a.status === 'investigating').length, col: 'text-blue-700 dark:text-blue-400' },
          ].map(({ label, value, col }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${col}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className={SELECT_CLS}>
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={SELECT_CLS}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="investigating">Action Taken</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
          </select>
          <select value={filterAsset} onChange={(e) => setFilterAsset(e.target.value)} className={SELECT_CLS}>
            <option value="">All Assets</option>
            {assets.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <Button size="sm" onClick={load}>Apply</Button>
          {(filterSeverity || filterStatus || filterAsset) && (
            <button onClick={() => { setFilterSeverity(''); setFilterStatus(''); setFilterAsset(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline">Clear</button>
          )}
        </div>
      </Card>

      {/* ── Encroachment table ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {anomalies.length} encroachment event{anomalies.length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-400">Click <strong>Escalate</strong> to take action</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : anomalies.length === 0 ? (
          <div className="text-center py-14">
            <AlertOctagon className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No encroachment events detected</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>Scan RoW (NDBI)</strong> to check for new encroachments</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {anomalies.map((a) => {
              const isExpanded = expandedId === a.id;
              return (
                <div key={a.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : a.id)}
                  >
                    <AlertOctagon className={`w-4 h-4 flex-shrink-0 ${a.severity === 'critical' ? 'text-red-500' : 'text-amber-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{a.boundary_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {a.anomaly_type?.replace(/_/g, ' ')} ·{' '}
                        {a.detection_date ? new Date(a.detection_date).toLocaleDateString('en-GB') : '—'}
                      </p>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[a.severity]}`}>{a.severity}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-3">
                      <div className="flex flex-wrap gap-6 text-xs text-gray-500 dark:text-gray-400">
                        <span>NDBI Δ: <strong className="text-orange-600 dark:text-orange-400">
                          {a.change_percentage != null ? `+${Number(a.change_percentage).toFixed(1)}%` : '—'}
                        </strong></span>
                        {a.affected_area_hectares != null && (
                          <span>Affected area: <strong className="text-gray-800 dark:text-gray-200">{Number(a.affected_area_hectares).toFixed(1)} ha</strong></span>
                        )}
                      </div>
                      {a.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic">{a.description}</p>
                      )}
                      {a.resolution_notes && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2">{a.resolution_notes}</p>
                      )}
                      <Button size="sm" variant={a.status === 'pending' ? 'primary' : 'outline'}
                        onClick={() => { setReviewing(a); setActionId(''); setActionNote(''); }}>
                        Escalate
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Escalation modal ── */}
      {reviewing && (
        <Modal onClose={() => setReviewing(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Escalate Encroachment</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{reviewing.boundary_name}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[reviewing.severity]}`}>
                {reviewing.severity}
              </span>
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 mb-4 text-sm">
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Satellite Analysis</p>
              <p className="text-gray-600 dark:text-gray-400">
                Type: <strong>{reviewing.anomaly_type?.replace(/_/g, ' ')}</strong> &nbsp;·&nbsp;
                NDBI Δ: <strong className="text-orange-600">{reviewing.change_percentage != null ? `+${Number(reviewing.change_percentage).toFixed(1)}%` : '—'}</strong>
                {reviewing.affected_area_hectares != null && (
                  <> &nbsp;·&nbsp; <strong>{Number(reviewing.affected_area_hectares).toFixed(1)} ha</strong> affected</>
                )}
              </p>
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Action</p>
              {ESCALATION_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setActionId(action.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-all ${actionId === action.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                    {action.label}
                    {action.badgeText && (
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${action.badgeClass}`}>{action.badgeText}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (optional)</label>
              <textarea rows={2} value={actionNote} onChange={(e) => setActionNote(e.target.value)}
                placeholder="Contractor reference, legal jurisdiction, escalation rationale…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleEscalate} disabled={!actionId || submitting} loading={submitting} className="flex-1">
                Confirm Action
              </Button>
              <Button variant="outline" onClick={() => setReviewing(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default EncroachmentDetectionPage;

// Made with Bob
